/**
 * MailDashboardPage — `/mail` index landing for the operator console.
 *
 * Ported from `screens/src/pages/mail/MailDashboard.jsx`. Aggregates a few KPIs
 * and links into the mail feature surface. Live wiring uses
 * `GET /v1/mail-items` and `GET /v1/drafts`; aggregator endpoints (today's
 * revenue, average processing time) are deferred and surface a soft notice.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  CheckCircle2,
  FileText,
  Mail as MailIcon,
  Package,
  Search,
  TrendingUp,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getMailDashboardStats, listMailItems } from '../../lib/api/mail-items';
import { listDrafts } from '../../lib/api/mail-drafts';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard, MailItemTable } from './shared/MailUI';

export function MailDashboardPage(): JSX.Element {
  const items = useQuery({
    queryKey: ['mail', 'items', { pageSize: 10 }],
    queryFn: () => listMailItems({ pageSize: 10 }),
    retry: false,
    staleTime: 30_000,
  });
  const drafts = useQuery({
    queryKey: ['mail', 'drafts', { pageSize: 1 }],
    queryFn: () => listDrafts({ pageSize: 1 }),
    retry: false,
    staleTime: 30_000,
  });
  const stats = useQuery({
    queryKey: ['mail', 'stats', 'dashboard', 30],
    queryFn: () => getMailDashboardStats(30),
    retry: false,
    staleTime: 30_000,
  });

  const itemsDenied =
    items.isError && items.error instanceof ApiError && (items.error.status === 401 || items.error.status === 403);
  const draftsDenied =
    drafts.isError && drafts.error instanceof ApiError && (drafts.error.status === 401 || drafts.error.status === 403);
  const statsDenied =
    stats.isError && stats.error instanceof ApiError && (stats.error.status === 401 || stats.error.status === 403);

  const weekTotal = stats.data
    ? stats.data.period.totalsByDay.slice(-7).reduce((acc, b) => acc + b.count, 0)
    : 0;
  const deliveredCount = stats.data?.today.byStatus['DELIVERED'] ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mail Operations"
        description="Counter activity, registered items, drafts, dispatch queue."
        actions={
          <>
            <Link
              to="/mail/register"
              className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
            >
              <MailIcon className="w-4 h-4" /> New Registration
            </Link>
            <Link
              to="/mail/drafts"
              className="inline-flex items-center gap-2 px-4 py-2 border border-border text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
            >
              <FileText className="w-4 h-4" /> View Drafts
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Mail Items"
          value={items.isPending ? '—' : itemsDenied ? '—' : String(items.data?.total ?? 0)}
          hint={itemsDenied ? 'Permission required' : 'All mail items in operator scope'}
          icon={MailIcon}
          tone={itemsDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Open Drafts"
          value={drafts.isPending ? '—' : draftsDenied ? '—' : String(drafts.data?.total ?? 0)}
          hint={draftsDenied ? 'Permission required' : 'Drafts not yet registered'}
          icon={FileText}
          tone={draftsDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Registered Today"
          value={stats.isPending ? '—' : statsDenied ? '—' : String(stats.data?.today.total ?? 0)}
          hint={statsDenied ? 'Permission required' : 'Items registered today'}
          icon={CalendarDays}
          tone={statsDenied ? 'warning' : 'default'}
        />
        <KPICard
          title="Last 7 Days"
          value={stats.isPending ? '—' : statsDenied ? '—' : String(weekTotal)}
          hint={statsDenied ? 'Permission required' : 'period.totalsByDay (7d)'}
          icon={TrendingUp}
          tone={statsDenied ? 'warning' : 'default'}
        />
      </div>

      {!statsDenied && stats.data ? (
        <section data-testid="mail-stats-panel" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-border rounded-xl p-4 lg:col-span-1">
            <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">
              Status Breakdown
            </p>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(stats.data.today.byStatus).length === 0 ? (
                <li className="text-xs text-muted italic">No mail items in scope.</li>
              ) : (
                Object.entries(stats.data.today.byStatus)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <li key={status} className="flex items-center justify-between">
                      <span className="font-mono text-xs text-gray-700">{status}</span>
                      <span className="font-semibold text-gray-900">{count}</span>
                    </li>
                  ))
              )}
            </ul>
            <p className="mt-3 text-[11px] text-muted">
              <CheckCircle2 className="inline w-3 h-3 mr-1 text-nipost-green" />
              {deliveredCount} delivered (all-time, in scope)
            </p>
          </div>

          <div className="bg-white border border-border rounded-xl p-4 lg:col-span-2">
            <div className="flex items-baseline justify-between mb-3">
              <p className="text-[10px] font-semibold text-muted uppercase tracking-widest">
                Last 30 days
              </p>
              <p className="text-[11px] text-muted">
                {stats.data.period.fromDate} → {stats.data.period.toDate}
              </p>
            </div>
            <DayBars buckets={stats.data.period.totalsByDay} />
          </div>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/mail/track"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          <Search className="w-4 h-4 text-nipost-green" /> Track a Package
        </Link>
        <Link
          to="/mail/sorting"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          <Package className="w-4 h-4 text-nipost-green" /> Sorting Workflow
        </Link>
        <Link
          to="/mail/items"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          <MailIcon className="w-4 h-4 text-nipost-green" /> All Mail Items
        </Link>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Mail Items</h2>
        {items.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : itemsDenied ? (
          <DeferredNotice title="Permission required">
            Your account does not have permission to view mail items. Please contact your administrator.
          </DeferredNotice>
        ) : items.isError ? (
          <DeferredNotice title="Service unreachable">
            {(items.error as Error).message}
          </DeferredNotice>
        ) : (
          <MailItemTable items={items.data?.items ?? []} emptyMessage="No mail items registered yet." />
        )}
      </div>
    </div>
  );
}

function DayBars({ buckets }: { buckets: readonly { date: string; count: number }[] }): JSX.Element {
  const max = buckets.reduce((m, b) => (b.count > m ? b.count : m), 1);
  return (
    <div className="flex items-end gap-1 h-24" data-testid="mail-stats-day-bars">
      {buckets.map((bucket) => {
        const height = `${Math.max(2, Math.round((bucket.count / max) * 100))}%`;
        return (
          <div
            key={bucket.date}
            className="flex-1 flex flex-col items-center justify-end"
            title={`${bucket.date} · ${bucket.count}`}
          >
            <div
              className={`w-full rounded-t-sm ${bucket.count > 0 ? 'bg-nipost-green/70' : 'bg-gray-100'}`}
              style={{ height }}
              aria-label={`${bucket.date}: ${bucket.count} items`}
            />
          </div>
        );
      })}
    </div>
  );
}
